<template>
    <div
        :class="{ 'mx-4': hasData }"
    >
        <slot name="top" :has-data="hasData"></slot>

        <!-- Spinner -->
        <div
            v-if="['idle', 'pending'].includes(productLoadStatus)"
            class="d-flex align-center justify-center"
        >
            <v-progress-circular
                size="42"
                color="accent"
                indeterminate
            ></v-progress-circular>
        </div>

        <!-- Header & List -->
        <template
            v-else-if="hasData"
        >
            <h3
                class="mb-4 text-uppercase text-center text-md-start"
            >
                You Might Also Like
            </h3>
            <div
                class="products-featured-scroller d-flex position-relative"
            >
                <SliderButton
                    class="scroll-btn-prev"
                    type="prev"
                    alt="Previous Slide"
                    @click.prevent="slideGo('prev')"
                ></SliderButton>
                <div
                    ref="elProductsFeaturedList"
                    class="products-featured-list carousel-h-scroll-parent pa-4"
                >
                    <div
                        class="fit-w-max-content d-flex gap-4"
                    >
                        <template
                            v-for="sku in productsList"
                            :key="sku.uid"
                        >
                            <ProductsFeaturedItem
                                :sku="sku"
                                use-default-image
                            ></ProductsFeaturedItem>
                        </template>
                    </div>
                </div>
                <SliderButton
                    class="scroll-btn-next"
                    type="next"
                    alt="Next Slide"
                    @click.prevent="slideGo('next')"
                ></SliderButton>
            </div>
        </template>
    </div>
</template>

<script lang="ts" setup>
    import type { Sku } from '~/types';
    import { useApiCommon } from '~/composables';
    import { isError as lo_isError } from 'es-toolkit';
    import ProductsFeaturedItem from '~/components/products/ProductsFeaturedItem/ProductsFeaturedItem.vue';
    import SliderButton from '~/components/carousel/SliderButton/SliderButton.vue';

    const props = defineProps<{
        productUid: string;
        marketUid?: Nilish<string>;
    }>();

    const scrollItemWidthInPx = 280; // Should be same as sass var "$products-featured-item-width"
    const elProductsFeaturedList = shallowRef<InstanceType<typeof HTMLElement>>();
    const useApiCommonObj = useApiCommon({ siteContext: 'cross_sell' });
    const queryObj = {
        uid: props.productUid,
        expand: [ 'default_image' ]
    };

    const {
        data: productsList,
        execute: getProductsListObj,
        pending: productsPending,
        status: productLoadStatus
    } = useLazyAsyncData<Sku[] | Error>(
        'keyProductsCrossSell',
        () => useApiCommonObj.getItem<Sku[]>(`/skus/cross-sell/`, queryObj),
        {
            server: false,
            immediate: false
        }
    );

    const hasData = computed<boolean>(() => Boolean(!lo_isError(productsList.value) && productsList.value?.length));

    function slideGo(dir: 'next' | 'prev') {
        const val = Math.abs(scrollItemWidthInPx) * (dir === 'next' ? 1 : -1);
        elProductsFeaturedList.value?.scrollBy({ top: 0, left: val, behavior: 'smooth' });
    }

    onMounted(() => {
        getProductsListObj();
    });
</script>

<style lang="scss">
.products-featured-scroller {
    padding: 0 30px;

    .scroll-btn-prev,
    .scroll-btn-next {
        position: absolute;
        z-index: 1;
        top: 0;
        bottom: 0;
        margin: auto;
        transform: scale(0.6);
    }

    .scroll-btn-prev {
        left: -28px;

    }
    .scroll-btn-next {
        right: -28px;
    }
}

.layout-mobile {
    .products-featured-scroller {
        padding: 0 !important;

        .scroll-btn-prev,
        .scroll-btn-next {
            display: none !important;
        }
    }
}

.products-featured-list {
    width: 100%;
}
</style>
